import { useCallback, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";

import { v4 as uuidv4 } from "uuid";
import { IButton, IMessage } from "../domain/message.interface";
import { WS_CHAT_URL } from "../infra/settings";
import {
  WebSocketPayload,
  WebSocketPayloadButton,
} from "../domain/webSocketPayload.interface";

const getThreadId = () => {
  let threadId = localStorage.getItem("threadId");
  if (!threadId) {
    threadId = uuidv4();
    localStorage.setItem("threadId", threadId);
  }
  return threadId;
};

const onInitChat = (callback: () => void) => {
  const chatInitied = localStorage.getItem("chatInitied");
  if (!chatInitied) {
    localStorage.setItem("chatInitied", "true");
    callback();
  }
  return !!chatInitied;
};

const saveMessageInLocalStorage = (message: IMessage) => {
  let storageMessages = localStorage.getItem("messages");
  let messages = [];

  if (storageMessages) {
    messages = JSON.parse(storageMessages);
  }

  messages.push(message);

  localStorage.setItem("messages", JSON.stringify(messages));
};

const loadMessagesFromLocalStorage = () => {
  let messages = localStorage.getItem("messages");
  if (!messages) {
    messages = JSON.stringify([]);
  }
  return JSON.parse(messages);
};

const useChatInteractions = () => {
  const initMessages = loadMessagesFromLocalStorage();

  const [messages, setMessages] = useState<IMessage[]>(initMessages);
  const [buttons, setButtons] = useState<string[]>([]);
  const [enabledInput, setEnabledInput] = useState(true);

  const [scrollLastMessage, setScrollLastMessage] = useState(false);

  const appendMessages = useCallback(
    (
      newMessages: IMessage[],
    ) => {
      for (const message of newMessages) {
        saveMessageInLocalStorage(message);
      }

      setMessages([
        ...messages,
        ...newMessages,
      ]);

      setScrollLastMessage(true);
    },
    [messages],
  );

  const { sendMessage, readyState } = useWebSocket(
    WS_CHAT_URL + "/websocket/" + getThreadId(),
    {
      shouldReconnect: () => true,
      reconnectAttempts: 1000,
      reconnectInterval: 10,
      onMessage: (event) => onMessageEvent(event),
    },
  );

  const convertButtonsFromWebsocket = (
    websocketButtons: WebSocketPayloadButton[],
  ): IButton[] => {
    const buttons: IButton[] = [];

    websocketButtons.map((button) => {
      buttons.push({
        subType: button.sub_type,
        options: button.options.map((option) => ({
          id: option.id,
          label: option.label,
        })),
      });
    });

    return buttons;
  };

  const onMessageEvent = (event: WebSocketEventMap["message"]) => {
    if (readyState === ReadyState.OPEN) {
      const {
        error,
        message,
        buttons,
      } = JSON
        .parse(event.data) as WebSocketPayload;
      if (!error) {
        appendMessages([
          buildMessage(message, false, convertButtonsFromWebsocket(buttons), Date.now()),
        ]);
      } else {
        appendMessages([
          buildMessage("Desculpe, não consegui entender.", false, [], Date.now()),
        ]);
      }
    }
  };

  const buildMessage = (
    message: string,
    userMessage: boolean,
    buttons: IButton[],
    timestamp: number,
  ): IMessage => {
    return {
      userMessage,
      content: message,
      buttons: buttons || [],
      timestamp: timestamp,
    };
  };

  const sendMessageToServer = (message: string): void => {
    sendMessage(
      JSON.stringify({
        message,
        threadId: getThreadId(),
        source: "febraban",
        userIdentifier: "notused",
      }),
    );
  };

  const handleChatSubmit = (message: string): void => {
    selectButtons([]);
    sendMessageToServer(message);
    appendMessages([buildMessage(message, true, [], Date.now())]);
  };

  const selectButtons = (buttons: string[]): void => {
    setButtons(buttons);
  };

  onInitChat(() => {
    sendMessageToServer("olá");
  });

  return {
    messages,
    buttons,
    enabledInput,
    handleChatSubmit,
    selectButtons,
    setEnabledInput,
  };
};

export default useChatInteractions;
export type { IMessage };
